const STORAGE_KEY_PREFIX = "classifieds-scroll-position:";
const HISTORY_STATE_KEY = "classifiedsScrollPositions";

type StoredScrollPosition = {
  top: number;
  loadedCount?: number;
};

const getStorageKey = (returnTo: string): string => `${STORAGE_KEY_PREFIX}${returnTo}`;

const clearHistoryScrollPosition = (returnTo: string): void => {
  const historyPositions = window.history.state?.[HISTORY_STATE_KEY];
  if (!historyPositions || typeof historyPositions !== "object" || !(returnTo in historyPositions)) return;

  const nextHistoryPositions = { ...historyPositions };
  delete nextHistoryPositions[returnTo];
  window.history.replaceState(
    {
      ...window.history.state,
      [HISTORY_STATE_KEY]: nextHistoryPositions,
    },
    "",
  );
};

const normalizeStoredPosition = (value: unknown): StoredScrollPosition | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) && value >= 0 ? { top: value } : null;
  }
  if (!value || typeof value !== "object") return null;

  const top = Number((value as StoredScrollPosition).top);
  const loadedCount = Number((value as StoredScrollPosition).loadedCount);
  if (!Number.isFinite(top) || top < 0) return null;

  return {
    top,
    loadedCount: Number.isFinite(loadedCount) && loadedCount > 0 ? Math.floor(loadedCount) : undefined,
  };
};

export const saveClassifiedsScrollPosition = (returnTo: string, loadedCount?: number): void => {
  if (typeof window === "undefined") return;

  try {
    const position: StoredScrollPosition = {
      top: Math.max(0, window.scrollY),
      loadedCount:
        typeof loadedCount === "number" && Number.isFinite(loadedCount) && loadedCount > 0
          ? Math.floor(loadedCount)
          : undefined,
    };
    window.sessionStorage.setItem(getStorageKey(returnTo), JSON.stringify(position));
    window.history.replaceState(
      {
        ...window.history.state,
        [HISTORY_STATE_KEY]: {
          ...window.history.state?.[HISTORY_STATE_KEY],
          [returnTo]: position,
        },
      },
      "",
    );
  } catch {
    // Scroll restoration is a progressive enhancement.
  }
};

export const getClassifiedsScrollPosition = (returnTo: string): StoredScrollPosition | null => {
  if (typeof window === "undefined") return null;

  const historyPosition = normalizeStoredPosition(
    window.history.state?.[HISTORY_STATE_KEY]?.[returnTo],
  );
  if (historyPosition) return historyPosition;

  try {
    const rawPosition = window.sessionStorage.getItem(getStorageKey(returnTo));
    if (!rawPosition) return null;
    return normalizeStoredPosition(JSON.parse(rawPosition));
  } catch {
    return null;
  }
};

export const restoreClassifiedsScrollPosition = (returnTo: string): (() => void) | undefined => {
  if (typeof window === "undefined") return undefined;

  const position = getClassifiedsScrollPosition(returnTo);
  try {
    window.sessionStorage.removeItem(getStorageKey(returnTo));
  } catch {}

  try {
    clearHistoryScrollPosition(returnTo);
  } catch {}

  if (!position) return undefined;
  const { top } = position;

  let secondFrameId = 0;
  const firstFrameId = window.requestAnimationFrame(() => {
    secondFrameId = window.requestAnimationFrame(() => {
      window.scrollTo({ top, left: 0, behavior: "auto" });
    });
  });

  return () => {
    window.cancelAnimationFrame(firstFrameId);
    if (secondFrameId) window.cancelAnimationFrame(secondFrameId);
  };
};
